import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import LeadForm from "@/components/site/LeadForm";
import EditorialMarquee from "@/components/site/EditorialMarquee";
import MonogramTile from "@/components/site/MonogramTile";
import FundingEstimator from "@/components/site/FundingEstimator";
import ClientWins from "@/components/site/ClientWins";
import HomeCaseStudies from "@/components/site/HomeCaseStudies";
import Seo from "@/lib/Seo";
import { SERVICES, PROCESS_STEPS, TESTIMONIALS } from "@/data/content";

const HERO_IMAGE =
  "https://images.pexels.com/photos/12471781/pexels-photo-12471781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const LINES = ["Steady capital.", "Uncommon paths.", "For the businesses", "that build the country."];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { delay: 0.15 + i * 0.12, duration: 0.9, ease: [0.7, 0, 0.2, 1] },
  }),
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden">
      {/* Cream-toned architectural texture with warm parallax */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt=""
          className="w-full h-[115%] object-cover opacity-[0.18]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--bg)]/40 via-[color:var(--bg)]/70 to-[color:var(--bg)]" />
      </motion.div>

      {/* Grid overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="max-w-[1400px] mx-auto h-full px-6 md:px-10 grid grid-cols-12 gap-4">
          {Array.from({ length: 11 }).map((_, i) => (
            <div key={i} className="border-l border-black/[0.05]" />
          ))}
        </div>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-4"
          >
            <span className="overline">A Brokerage · Est. Chicagoland</span>
          </motion.div>

          <h1 className="mt-8 font-serif tracking-tight leading-[0.95] text-[color:var(--bone)] text-[clamp(48px,8vw,120px)]">
            {LINES.map((line, i) => (
              <div key={line} className="reveal-mask">
                <motion.span
                  custom={i}
                  initial="hidden"
                  animate="show"
                  variants={lineVariants}
                  className="block"
                >
                  {i === 1 ? (
                    <em className="italic font-medium text-[color:var(--gold)]">{line}</em>
                  ) : (
                    line
                  )}
                </motion.span>
              </div>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 max-w-xl text-[color:var(--bone)]/75 text-lg leading-relaxed"
          >
            Steady Path Funding is an independent brokerage. We shop your file
            across 75+ vetted lenders — SBA, banks, and private capital — so you
            see real offers, side by side, in plain English.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <Link
              to="/apply"
              className="group inline-flex items-center gap-3 bg-[#3d3529] text-white px-8 py-4 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold)] hover:text-black transition-colors"
            >
              Start Application
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/products"
              className="gold-link font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--bone)]/80"
            >
              Explore Products
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="mt-16 grid grid-cols-3 gap-8 max-w-lg border-t border-black/10 pt-8"
          >
            {[
              ["75+", "Lending partners"],
              ["No min", "FICO score"],
              ["24 hr", "Fastest funding"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-serif text-3xl md:text-4xl text-[color:var(--bone)]">{n}</div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]/55">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="lg:col-span-5 lg:mt-4"
        >
          <LeadForm />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-6 md:left-10 font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--bone)]/50">
        Scroll · 01 / 07
      </div>
    </section>
  );
};

const Manifesto = () => (
  <section className="py-32 md:py-44 border-t border-black/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
      <div className="col-span-12 md:col-span-4">
        <div className="overline">Manifesto</div>
        <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight">Four steps, no theatre.</h2>
        <p className="mt-6 text-[color:var(--bone)]/70 max-w-sm">
          The brokerage model is simple. It only feels complicated because most
          brokers make it that way. Here's how we don't.
        </p>
        <Link
          to="/process"
          className="inline-flex items-center gap-2 mt-8 font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)] gold-link"
        >
          Read the full process →
        </Link>
      </div>
      <div className="col-span-12 md:col-span-8 space-y-16">
        {PROCESS_STEPS.map((s, i) => (
          <motion.div
            key={s.num}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: i * 0.06, ease: [0.7, 0, 0.2, 1] }}
            className="grid grid-cols-12 gap-6 items-start border-t border-black/10 pt-10"
          >
            <div className="col-span-3 font-mono text-[color:var(--gold)] text-lg tracking-widest">{s.num}</div>
            <div className="col-span-9">
              <h3 className="font-serif text-3xl md:text-4xl leading-tight text-[color:var(--bone)]">{s.title}</h3>
              <p className="mt-4 text-[color:var(--bone)]/70 max-w-xl">{s.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const ProductsTeaser = () => (
  <section className="py-32 md:py-44 border-t border-black/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
        <div>
          <div className="overline">Capital, by product</div>
          <h2 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95] max-w-xl">
            Fourteen ways to fund <em className="italic text-[color:var(--gold)]">what's next</em>.
          </h2>
        </div>
        <Link
          to="/products"
          className="font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)] gold-link"
        >
          All 14 products →
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        {SERVICES.slice(0, 6).map((s, i) => (
          <motion.div
            key={s.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: i * 0.05 }}
            className={`group relative overflow-hidden border border-black/10 hover:border-[color:var(--gold)]/60 transition-colors duration-500 ${
              i === 0 ? "md:col-span-4 md:row-span-2" : i === 1 ? "md:col-span-2" : "md:col-span-2"
            }`}
            style={{ minHeight: i === 0 ? 460 : 220 }}
          >
            <img
              src={s.image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-65 group-hover:opacity-100 transition-all duration-[900ms] group-hover:scale-105 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bg)] via-[color:var(--bg)]/70 to-[color:var(--bg)]/20" />
            <div className="relative h-full p-6 md:p-8 flex flex-col justify-between">
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--gold)]">
                {s.number} · {s.subtitle}
              </div>
              <div>
                <h3 className="font-serif text-3xl md:text-4xl leading-[1.05] text-[color:var(--bone)]">
                  {s.title}
                </h3>
                <Link
                  to={`/products#${s.id}`}
                  className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase text-[color:var(--bone)]/80 hover:text-[color:var(--gold)] transition-colors"
                >
                  Learn more →
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const TestimonialsStrip = () => (
  <section className="py-32 md:py-44 border-t border-black/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
      <div className="col-span-12 md:col-span-4">
        <div className="overline">Clients</div>
        <h2 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95]">
          The <em className="italic text-[color:var(--gold)]">work</em>, in their words.
        </h2>
      </div>
      <div className="col-span-12 md:col-span-8 space-y-10">
        {TESTIMONIALS.slice(0, 2).map((t, i) => (
          <motion.figure
            key={t.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.08 }}
            className="grid grid-cols-12 gap-6 border-t border-black/10 pt-10"
          >
            <div className="col-span-4 md:col-span-3">
              <div className="aspect-[4/5] overflow-hidden">
                <MonogramTile initials={t.initials} accent={t.accent} />
              </div>
            </div>
            <div className="col-span-8 md:col-span-9">
              <blockquote className="font-serif text-2xl md:text-3xl leading-snug text-[color:var(--bone)]">
                “{t.quote}”
              </blockquote>
              <figcaption className="mt-6 font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60">
                {t.name} · {t.role}
              </figcaption>
            </div>
          </motion.figure>
        ))}
      </div>
    </div>
  </section>
);

const CTAStrip = () => (
  <section className="border-t border-black/10 bg-[color:var(--gold)] text-black">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
      <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] max-w-2xl">
        The next chapter needs capital.
      </h2>
      <div className="flex items-center gap-6">
        <Link
          to="/apply"
          className="inline-flex items-center gap-3 bg-[#3d3529] text-white px-8 py-4 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[#232019] transition-colors"
        >
          Apply now <ArrowRight size={14} />
        </Link>
        <Link
          to="/contact"
          className="font-mono text-xs tracking-[0.24em] uppercase border-b border-black pb-1"
        >
          Talk to a broker
        </Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div>
      <Seo path="/" />
      <Hero />
      <EditorialMarquee />
      <FundingEstimator />
      <Manifesto />
      <ClientWins />
      <HomeCaseStudies />
      <ProductsTeaser />
      <CTAStrip />
    </div>
  );
}
